import { LoadIcon } from '../bs-icons/loading'
import { SEPARATOR_TEXT } from './constants'
import LlmIcon from './LlmIcon'
import Separator from './Separator'
import { ContentType } from './types'

/**
 * 系统消息组件
 *
 * 这是聊天系统中用于显示系统消息的组件，包括加载状态、分隔符和系统通知等。
 * 支持多种系统消息类型，如加载指示器、分隔线、系统通知等。
 *
 * 功能特点：
 * - 支持加载状态显示
 * - 支持分隔符消息
 * - 支持系统通知消息
 * - 支持自定义Logo显示
 * - 响应式布局设计
 *
 * 使用场景：
 * 1. 系统加载状态 - 显示系统正在处理中的状态
 * 2. 消息分隔符 - 在聊天记录中插入分隔线
 * 3. 系统通知 - 显示系统级别的通知消息
 * 4. 错误提示 - 显示系统错误或警告信息
 * 5. 状态更新 - 显示系统状态变化信息
 * 6. 连接状态 - 显示网络连接状态信息
 * 7. 会话管理 - 显示会话开始/结束信息
 * 8. 功能提示 - 显示功能使用提示信息
 *
 * 使用示例：
 * ```tsx
 * // 加载状态
 * <MessageSystem
 *   data={{ text: '', type: 'text' }}
 *   logo="system-logo.png"
 * />
 *
 * // 分隔符
 * <MessageSystem
 *   data={{ text: '--- 新会话开始 ---', type: 'text' }}
 * />
 *
 * // 系统通知
 * <MessageSystem
 *   data={{ text: '系统维护中，请稍后再试', type: 'text' }}
 *   logo="warning-icon.png"
 * />
 * ```
 *
 * @param data - 消息内容数据，包含文本和类型信息
 * @param logo - 可选的系统Logo图片URL
 */
export default function MessageSystem({ data, logo }: { data: ContentType; logo?: string }) {
  // 获取消息文本内容
  const msg = data.text

  // 默认内容：显示加载图标
  let content = (
    <div className="w-fit max-w-[90%]">
      <div>
        <LoadIcon className="text-gray-400" />
      </div>
    </div>
  )

  // 根据消息类型渲染不同内容
  if (msg === SEPARATOR_TEXT) {
    // 分隔符消息：使用Separator组件渲染
    content = <Separator text={msg} />
  } else if (msg) {
    // 系统通知消息：显示完整的消息内容
    content = (
      <div className="w-fit max-w-[90%]">
        {/* 系统消息头部：Logo和角色名称 */}
        <div className="flex gap-[12px]">
          {logo ? (
            // 使用自定义Logo
            <img className="flex h-6 w-6 min-w-6 items-center justify-center rounded-[12px]" src={logo} alt="" />
          ) : (
            // 使用默认AI图标
            <LlmIcon avatarColor="#111111" />
          )}

          <span className="chat-role-name">智能助手</span>
        </div>

        {/* 系统消息内容区域 */}
        <div className="chat-custom-content ml-[34px] min-h-8 rounded-[12px] bg-[#FFFFFF] p-[16px] dark:bg-[#313336]">
          <div className={`${msg.toString() ? 'flex gap-2' : 'flexRowStart gap-2'} break-words break-all`}>
            {/* <LlmIcon avatarColor="#111111" /> */}
            {msg}
          </div>
        </div>
      </div>
    )
  }

  return <div className="py-1">{content}</div>
}
